import type { BulkImportRow, ImportProgress, ImportResult } from "./types";

export const initializeProgresses = <T extends { id: string; label: string }>(
  rows: T[],
): Map<string, ImportProgress> => {
  return new Map(
    rows.map((row) => [
      row.id,
      { id: row.id, label: row.label, status: "pending" as const },
    ]),
  );
};

export const createWithProgress = async <TRow extends BulkImportRow, TResult>(
  rows: TRow[],
  createFn: (row: TRow) => Promise<TResult>,
  updateProgress: (id: string, update: Partial<ImportProgress>) => void,
): Promise<ImportResult<TResult>> => {
  const settlements = await Promise.allSettled(
    rows.map(async (row) => {
      updateProgress(row.id, { status: "creating" });
      const result = await createFn(row);
      updateProgress(row.id, { status: "completed" });
      return { id: row.id, result };
    }),
  );

  const successful: ImportResult<TResult>["successful"] = [];
  const failed: ImportResult<TResult>["failed"] = [];

  settlements.forEach((settlement, i) => {
    const row = rows[i];
    if (settlement.status === "fulfilled") {
      successful.push(settlement.value);
    } else {
      const error =
        settlement.reason instanceof Error
          ? settlement.reason.message
          : String(settlement.reason);
      updateProgress(row.id, { status: "failed", error });
      failed.push({ id: row.id, label: row.label, error });
    }
  });

  return { successful, failed };
};

export const handleImportResults = (
  result: ImportResult<unknown>,
  total: number,
  toast: { success: (msg: string) => void; warning: (msg: string) => void },
): void => {
  const { successful, failed } = result;
  if (failed.length === 0) {
    toast.success(
      `${successful.length} ${successful.length === 1 ? "item" : "items"} imported successfully.`,
    );
  } else {
    toast.warning(
      `${successful.length} of ${total} imported. ${failed.length} failed.`,
    );
  }
};
